///
/// EarthBrowser
///
/// Created by Matt Giger on 1/28/2024
/// Copyright (c) 2024 Geodata Labs
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer, type RenderActionSet } from "@stovokor/engine/Renderer"
import { NodeMatrixCache, type RenderState } from "@stovokor/engine/RenderState"
import { type GestureRecognizerHolder } from "@stovokor/gestures/GestureNode"
import { type AnyGestureRecognizer, type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { rect } from "@stovokor/math/rect"
import { sphere } from "@stovokor/math/sphere"
import { vec3, vec4 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { StovokorContext } from "@stovokor/StovokorContext"
import { type Canvas, type CanvasState } from "@stovokor/uipanes/Canvas"

// rules for binding to parent during size changes for Panes
export enum bind {
	none			= 0x0000,
	left			= 0x0001,
	top				= 0x0002,
	right			= 0x0004,
	bottom			= 0x0008,
	width			= 0x0010,
	height			= 0x0020,
}

export class Pane extends NodeBase implements Material, GestureRecognizerHolder {
	public bindings: bind = bind.none
	public quadTexture: Texture
	public textureFrame = new vec4(0, 0, 1, 1)
	public transparency = true
	public available: boolean = true

	protected _contents?: Canvas
	protected _frame = new rect()
	protected _depth: number = 3
	protected _imageURL?: string
	protected adoptIntrinsicSize: boolean = false

	protected context: StovokorContext

	// Children are pushed under the non-scaling transform, a second
	// coordinate space with its own matrix memo
	private readonly childrenMatrixCache = new NodeMatrixCache()
	private offsetSource?: matrix

	// Gesture recognizer support
	private _recognizers: AnyGestureRecognizer[] = []

	constructor(context: StovokorContext) {
		super()

		this.context = context
		this.quadTexture = new Texture(context)
		this.renderer = new PaneRenderer(context, this)
	}

	// ========== GestureRecognizerHolder Implementation ==========

	public get recognizers(): AnyGestureRecognizer[] {
		return this._recognizers
	}

	public addRecognizer(recognizer: AnyGestureRecognizer): void {
		recognizer.owner = this as unknown as import("@stovokor/gestures/GestureNode").GestureNode
		this._recognizers.push(recognizer)
	}

	public removeRecognizer(recognizer?: AnyGestureRecognizer): void {
		if(!recognizer) return

		const index = this._recognizers.indexOf(recognizer)
		if (index !== -1) {
			this._recognizers[index].owner = undefined
			this._recognizers.splice(index, 1)
		}
	}

	/**
	 * 2D gesture hit test for panes
	 *
	 * Uses simple containment testing with the pane's frame and global offset.
	 * Returns a hit result if the point is within the pane and the pane has recognizers.
	 */
	public gestureHitTest2D(x: number, y: number): GestureHitResult | undefined {
		if (!this.visible || this._recognizers.length === 0) {
			return undefined
		}

		// Check if point is within our frame (using global offset from rendering)
		const offsetX = x - this._globalOffset.x
		const offsetY = y - this._globalOffset.y

		if (!this._frame.offsetContains(offsetX, offsetY)) {
			return undefined
		}

		// Return hit with local coordinates and depth-based distance
		// Lower depth values = closer to camera in 2D, so use negative depth for sorting
		return {
			node: this as unknown as import("@stovokor/gestures/GestureNode").GestureNode,
			hitPoint: new vec3(offsetX, offsetY, 0),
			distance: -this._globalOffset.z  // Frontmost panes have higher z, so negate for sorting
		}
	}

	/**
	 * Adds a child node to the Pane.
	 *
	 * @template T - The type of the child node.
	 * @param {T | T[]} child - The child node or an array of child nodes to add.
	 * @returns {T} - The added child node.
	 */
	override addNode<T extends NodeBase>(child: T | T[]): T {
		let ret: T = child as T
		if(child instanceof Pane) {
			super.addNode(child)
		} else if(child instanceof Array && child.every(c => c instanceof Pane)) {
			super.addNode(child)
			ret = child[child.length - 1] as T
		} else {
			this.context.logger.error(`PaneNode only supports PaneNode children`)
		}

		// children are distributed evenly in depth from 0 to 1
		const paneChildren = this._children as Pane[]
		paneChildren.forEach((c, index, array) => {
			c.depth = index * 0.01 + (0.99 / array.length)
		})

		return ret
	}

	/**
	 * Gets the URL of the image.
	 *
	 * @returns The URL of the image, or undefined if no image URL is set.
	 */
	public get imageUrl(): string | undefined {
		return this._imageURL
	}

	public set imageUrl(value: string | undefined) {
		if(value !== this._imageURL) {
			this._imageURL = value
			if(value) {
				this.quadTexture.loadUrl(value).catch(_e => undefined)
			} else {
				this.quadTexture.destroy()
			}
		}
	}

	public contents(contents?: Canvas): Pane {
		this._contents = contents
		if(!contents) {
			this.quadTexture.destroy()
		}
		return this
	}

	// Render the pane into our context
	protected updateContents(): void {
		if(!this._contents || !this._frame.notEmpty) return
		// Rasterize at the drawing buffer's pixel ratio so one logical pixel
		// of the pane maps to one device pixel; a ratio change (monitor
		// move, zoom) re-rasterizes even when the contents didn't change
		const scale = this.context.pixelRatio
		if(this._contents.needsUpdate || this._contents.scale !== scale) {
			const intrinsicDifferent = this._contents.rerender(this._frame.width, this._frame.height, scale)
			if(this.adoptIntrinsicSize && intrinsicDifferent) {
				this.size(this._contents.width, this._contents.height)
			}
			if(!this._frame.empty) {
				// Upload the canvas itself: texImage2D reads it directly,
				// avoiding the full-surface CPU copy getImageData makes. The
				// surface is rasterized at the drawing buffer's pixel ratio
				// and drawn 1:1, so it never minifies: skip the mipmaps
				this.quadTexture.uploadCanvas(this._contents.canvas, this._contents.surfaceWidth, this._contents.surfaceHeight, false)
			}
		}
	}

	public get contentState(): CanvasState {
		return this._contents?.state ?? "normal"
	}
	public set contentState(state: CanvasState) {
		if(this._contents) {
			this._contents.state = state
		}
	}

	override render(state: RenderState): RenderActionSet[] {
		if(!this.visible) {
			return []
		}

		this.updateContents()

		let actions: RenderActionSet[] = []
		if(this._contents || this.quadTexture.available) {
			state.pushTransform(this._transform.transform, this.matrixCache)
			actions = this.renderActions(state)
			state.popTransform()
		}

		// Render children with a non-scaling transform.
		// A pane's scale is used to size the contents of this pane
		// the children's scale should be independent of the parent
		const model = state.pushTransform(this._transform.nonScalingTransform, this.childrenMatrixCache)
		if(this.childrenMatrixCache.model !== this.offsetSource) {
			this._globalOffset = model.position
			this.offsetSource = model
		}
		actions = actions.concat(this.renderChildren(state))
		state.popTransform()

		return actions
	}

	/**
	 * Gets the frame of the pane. Please do not modify the properties of the returned rect.
	 */
	public get frame(): rect {
		return this._frame
	}
	public get left(): number {
		return this._frame.left
	}
	public get top(): number {
		return this._frame.top
	}
	public get width(): number {
		return this._frame.width
	}
	public get height(): number {
		return this._frame.height
	}
	public get depth(): number {
		return this._depth
	}
	public set depth(value: number) {
		if(value !== this._depth) {
			this._depth = value
			this._transform.translate(this._frame.left, this._frame.top, value)
		}
	}

	/**
	 * Binds the given rules to the pane.
	 *
	 * @param rules - The rules to bind.
	 * @returns The pane with the bindings applied.
	 */
	public bind(rules: bind): Pane {
		this.bindings = rules
		return this
	}

	/**
	 * Sets the offset of the pane.
	 *
	 * @param left - The left offset.
	 * @param top - The top offset.
	 * @returns The updated Pane instance.
	 */
	public offset(left: number, top: number): Pane {
		if(left !== this._frame.left || top !== this._frame.top) {
			this._frame.left = left
			this._frame.top = top
			this._transform.translate(left, top, this._depth)
		}
		return this
	}

	/**
	 * Sets the size of the pane.
	 *
	 * @template T - The type of the pane.
	 * @param {number} width - The new width of the pane.
	 * @param {number} height - The new height of the pane.
	 * @returns {T} - The updated pane.
	 */
	public size(width: number, height: number): Pane {
		if(width !== this._frame.width || height !== this._frame.height) {
			const deltaWidth = width - this._frame.width
			const deltaHeight = height - this._frame.height
			this._contents?.setNeedsUpdate()
			this._frame.width = width
			this._frame.height = height
			this._transform.scale(width, height, 1)

			const paneChildren = this._children as Pane[]
			paneChildren.forEach(c => c.parentResized(this._frame.width, this._frame.height, deltaWidth, deltaHeight))
		}
		return this
	}

	/**
	 * Sets the image URL for the pane.
	 *
	 * @param url - The URL of the image.
	 * @returns The updated Pane instance.
	 */
	public image(url: string): Pane {
		this.imageUrl = url
		return this
	}

	/**
	 * Called when the parent of the pane is resized. Update children based on their bindings.
	 *
	 * @param width - The new width of the parent.
	 * @param height - The new height of the parent.
	 * @param deltaWidth - The change in width of the parent.
	 * @param deltaHeight - The change in height of the parent.
	 */
	override parentResized(_width: number, _height: number, deltaWidth: number, deltaHeight: number): void {
		const b = this.bindings
		let xdelta = 0, ydelta = 0, wdelta = 0, hdelta = 0
		if(b & bind.right) {
			if(b & bind.left) {
				xdelta = deltaWidth / 2
			} else {
				xdelta = deltaWidth
			}
		}
		if(b & bind.bottom) {
			if(b & bind.top) {
				ydelta = deltaHeight / 2
			} else {
				ydelta = deltaHeight
			}
		}
		if(b & bind.width) {
			wdelta = deltaWidth
		}
		if(b & bind.height) {
			hdelta = deltaHeight
		}

		if(xdelta != 0 || ydelta != 0) {
			this._frame.left += xdelta
			this._frame.top += ydelta
			this._transform.translate(this._frame.left, this._frame.top, this._depth)
		}
		if(wdelta != 0 || hdelta != 0) {
			this._frame.width += wdelta
			this._frame.height += hdelta
			this._transform.scale(this._frame.width, this._frame.height, 1)
			this._contents?.setNeedsUpdate()

			const paneChildren = this._children as Pane[]
			paneChildren.forEach(c => c.parentResized(this._frame.width, this._frame.height, wdelta, hdelta))
		}
	}
}

export class PaneModel implements Geometry {
	public mode: DrawMode = DrawMode.Triangles
	public position: VertexAttribute
	public texCoord: VertexAttribute
	public vertexBuffer: VertexBuffer
	public available: boolean = true
	public bounds = new cube(0, 1)
	public boundingSphere = new sphere(0.5, new vec3(0.5, 0.5, 0))

	constructor(context: StovokorContext) {
		this.position = new VertexAttribute(context, 2, [0, 0, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1])
		this.texCoord = new VertexAttribute(context, 2, [0, 0, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1])
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.texCoord])
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
	}
}

export class PaneRenderer extends Renderer {
	protected pane: Pane
	protected modelViewProjection = new UniformDynamic<matrix>()

	constructor(context: StovokorContext, pane: Pane) {
		const source = new ShaderSourceDefinition("quad")
		super(context, source, context.paneModel, [pane])
		this.pane = pane
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
	}
}

export class DepthPaneRenderer extends Renderer {
	protected pane: Pane
	protected modelViewProjection = new UniformDynamic<matrix>()
	public worldDepth = new UniformDynamic<number>()

	constructor(context: StovokorContext, pane: Pane) {
		const source = new ShaderSourceDefinition("quad_depth")
		super(context, source, context.paneModel, [pane])
		this.pane = pane
		this.worldDepth.value = 1.0
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
	}
}